import Header from '../components/Header'

export default function Methodology() {
  return (
    <div className="min-h-screen bg-[var(--color-bg-dark)]">
      <Header onSearch={() => {}} />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-[var(--color-text)] mb-8">Metodología</h1>

        {/* Data Sources */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-[var(--color-primary)] mb-4">
            Fuentes de Datos
          </h2>
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-[var(--color-text)] mb-4">
              Crimen en México utiliza datos públicos de incidencia delictiva de las siguientes fuentes oficiales:
            </p>
            <ul className="space-y-3 text-[var(--color-text-muted)]">
              <li className="flex items-start gap-3">
                <span className="text-[var(--color-primary)]">•</span>
                <div>
                  <strong className="text-[var(--color-text)]">SESNSP</strong> - Secretariado Ejecutivo del Sistema Nacional de Seguridad Pública.
                  Datos mensuales de incidencia delictiva a nivel estatal y municipal.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--color-primary)]">•</span>
                <div>
                  <strong className="text-[var(--color-text)]">INEGI</strong> - Instituto Nacional de Estadística y Geografía.
                  Datos de defunciones por homicidio basados en certificados de defunción.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--color-primary)]">•</span>
                <div>
                  <strong className="text-[var(--color-text)]">datos.gob.mx</strong> - Portal de datos abiertos del gobierno de México.
                  Archivos CSV con datos históricos desde 2015.
                </div>
              </li>
            </ul>
          </div>
        </section>

        {/* Data Reliability */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-[var(--color-primary)] mb-4">
            Confiabilidad de los Datos
          </h2>
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-[var(--color-text)] mb-4">
              Es importante entender las diferencias entre las fuentes de datos:
            </p>

            <div className="space-y-4">
              <div className="bg-[var(--color-bg-dark)] rounded-lg p-4">
                <h3 className="text-[var(--color-text)] font-medium mb-2">
                  Datos del SESNSP (Carpetas de Investigación)
                </h3>
                <p className="text-[var(--color-text-muted)] text-sm">
                  Provienen de las fiscalías estatales. Pueden estar sujetos a subregistro ya que
                  aproximadamente el 90% de los delitos en México no se denuncian. Además, las
                  fiscalías pueden tener incentivos para reducir las cifras reportadas.
                </p>
              </div>

              <div className="bg-[var(--color-bg-dark)] rounded-lg p-4">
                <h3 className="text-[var(--color-text)] font-medium mb-2">
                  Datos del INEGI (Certificados de Defunción)
                </h3>
                <p className="text-[var(--color-text-muted)] text-sm">
                  Considerados más confiables para homicidios porque los médicos forenses no tienen
                  los mismos incentivos que las fiscalías para alterar las cifras. Sin embargo,
                  estos datos tienen aproximadamente un año de retraso.
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-400 text-sm">
                <strong>Nota:</strong> Cuando sea posible, comparamos ambas fuentes para identificar
                discrepancias. Las diferencias significativas pueden indicar problemas de registro.
              </p>
            </div>
          </div>
        </section>

        {/* Crime Categories */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-[var(--color-primary)] mb-4">
            Categorías de Delitos
          </h2>
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-[var(--color-text)] mb-4">
              Los delitos se clasifican según las categorías del SESNSP:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-[var(--color-danger)] font-medium mb-2">Delitos de Alto Impacto</h3>
                <ul className="space-y-1 text-[var(--color-text-muted)] text-sm">
                  <li>• Homicidio doloso</li>
                  <li>• Feminicidio</li>
                  <li>• Secuestro</li>
                  <li>• Extorsión</li>
                  <li>• Violación</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[var(--color-warning)] font-medium mb-2">Delitos Patrimoniales</h3>
                <ul className="space-y-1 text-[var(--color-text-muted)] text-sm">
                  <li>• Robo de vehículo</li>
                  <li>• Robo a casa habitación</li>
                  <li>• Robo a negocio</li>
                  <li>• Robo a transeúnte</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[var(--color-primary)] font-medium mb-2">Otros Delitos</h3>
                <ul className="space-y-1 text-[var(--color-text-muted)] text-sm">
                  <li>• Lesiones dolosas</li>
                  <li>• Violencia familiar</li>
                  <li>• Narcomenudeo</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Rate Calculations */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-[var(--color-primary)] mb-4">
            Cálculo de Tasas
          </h2>
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-[var(--color-text)] mb-4">
              Las tasas de criminalidad se calculan por cada 100,000 habitantes:
            </p>
            <div className="bg-[var(--color-bg-dark)] rounded-lg p-4 font-mono text-sm text-[var(--color-primary)]">
              Tasa = (Número de delitos / Población) × 100,000
            </div>
            <p className="text-[var(--color-text-muted)] mt-4 text-sm">
              Los datos de población provienen del Censo de Población y Vivienda 2020 del INEGI,
              con proyecciones para años posteriores basadas en las tasas de crecimiento estimadas
              por CONAPO.
            </p>
          </div>
        </section>

        {/* Data Processing */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-[var(--color-primary)] mb-4">
            Proceso de Actualización
          </h2>
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <ol className="space-y-3 text-[var(--color-text-muted)]">
              <li className="flex items-start gap-3">
                <span className="bg-[var(--color-primary)] text-black w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <span>Descarga mensual de archivos CSV desde datos.gob.mx</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[var(--color-primary)] text-black w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <span>Validación y limpieza de datos (valores faltantes, inconsistencias)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[var(--color-primary)] text-black w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <span>Agregación por estado, municipio y tipo de delito</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[var(--color-primary)] text-black w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                <span>Cálculo de tasas usando datos de población actualizados</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[var(--color-primary)] text-black w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                <span>Almacenamiento en base de datos y actualización de la API</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Limitations */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-[var(--color-primary)] mb-4">
            Limitaciones
          </h2>
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <ul className="space-y-3 text-[var(--color-text-muted)]">
              <li className="flex items-start gap-3">
                <span className="text-[var(--color-danger)]">•</span>
                <span><strong className="text-[var(--color-text)]">Cifra negra:</strong> Se estima que aproximadamente 90% de los delitos no se denuncian en México.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--color-danger)]">•</span>
                <span><strong className="text-[var(--color-text)]">Retraso en datos:</strong> Los datos oficiales tienen un retraso de 1-2 meses.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--color-danger)]">•</span>
                <span><strong className="text-[var(--color-text)]">Reclasificación:</strong> Algunos delitos pueden ser reclasificados posteriormente.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--color-danger)]">•</span>
                <span><strong className="text-[var(--color-text)]">Variabilidad regional:</strong> La calidad del registro varía significativamente entre estados.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="mb-10">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-red-400 mb-3">
              Aviso Legal
            </h2>
            <p className="text-[var(--color-text-muted)] text-sm">
              Este sitio muestra datos estadísticos derivados únicamente con fines educativos y de investigación.
              Los datos provienen de fuentes gubernamentales públicas y no garantizamos su exactitud o completitud.
              Esta información no debe utilizarse como única fuente para tomar decisiones que afecten la seguridad
              personal. Consulte siempre fuentes oficiales y autoridades locales para información actualizada
              sobre seguridad en su área.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-[var(--color-text-muted)]">
          <p>
            ¿Tienes preguntas sobre nuestra metodología?{' '}
            <a href="mailto:contacto@crimenmexico.com" className="text-[var(--color-primary)] hover:underline">
              Contáctanos
            </a>
          </p>
        </footer>
      </main>
    </div>
  )
}
